import { Table, Tooltip, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useMemo } from 'react'
import shortenText from '../../arendeFordelning/components/tableUtils'
import { FormattedJsonData } from '../utils/jsonFormatter'

interface TaskDistributionTableProps {
  timepointTasks: FormattedJsonData['timepointTasks']
  extraTasks?: FormattedJsonData['extraTasks']
  timepointOrder?: string[]
  taskOrder?: string[]
  extraTaskOrder?: string[]
}

interface DistributionRow {
  key: number
  [timepoint: string]: string | number
}

const COLUMN_WIDTH = 150
const EXTRA_TASKS_KEY = '__extraTasks__'
const EXTRA_TASKS_TITLE = 'Övriga insatser'
const TASK_NAME_MAX_LENGTH = 18

const renderTaskName = (value: string | number) => {
  if (typeof value !== 'string' || !value) return value
  const shortened = shortenText(value, TASK_NAME_MAX_LENGTH)
  if (shortened === value) return value
  return <Tooltip title={value}>{shortened}</Tooltip>
}

const sortByReference = (present: string[], reference?: string[]) => {
  if (!reference) return present
  const ordered = reference.filter((name) => present.includes(name))
  const missing = present.filter((name) => !reference.includes(name))
  return [...ordered, ...missing]
}

const TaskDistributionTable = ({ timepointTasks, extraTasks, timepointOrder, taskOrder, extraTaskOrder }: TaskDistributionTableProps) => {
  const { columns, dataSource, tableWidth } = useMemo(() => {
    const sortedTimepoints = sortByReference(Object.keys(timepointTasks), timepointOrder)

    const taskNamesByTimepoint: Record<string, string[]> = {}
    for (const timepoint of sortedTimepoints) {
      taskNamesByTimepoint[timepoint] = sortByReference(Object.keys(timepointTasks[timepoint]), taskOrder)
    }

    const sortedExtraTasks = extraTasks ? sortByReference(Object.keys(extraTasks), extraTaskOrder) : []
    const hasExtraColumn = sortedExtraTasks.length > 0

    if (sortedTimepoints.length === 0 && !hasExtraColumn) {
      return { columns: [], dataSource: [], tableWidth: 0 }
    }

    const maxRows = Math.max(
      ...sortedTimepoints.map((tp) => taskNamesByTimepoint[tp].length),
      hasExtraColumn ? sortedExtraTasks.length : 0,
      0
    )

    const columns: ColumnsType<DistributionRow> = [
      ...sortedTimepoints.map((timepoint) => ({
        title: timepoint,
        dataIndex: timepoint,
        key: timepoint,
        width: COLUMN_WIDTH,
        render: renderTaskName,
      })),
      ...(hasExtraColumn
        ? [
            {
              title: EXTRA_TASKS_TITLE,
              dataIndex: EXTRA_TASKS_KEY,
              key: EXTRA_TASKS_KEY,
              width: COLUMN_WIDTH,
              render: renderTaskName,
            },
          ]
        : []),
    ]

    const dataSource: DistributionRow[] = Array.from({ length: maxRows }, (_, rowIndex) => {
      const row: DistributionRow = { key: rowIndex }
      for (const timepoint of sortedTimepoints) {
        row[timepoint] = taskNamesByTimepoint[timepoint][rowIndex] ?? ''
      }
      if (hasExtraColumn) {
        row[EXTRA_TASKS_KEY] = sortedExtraTasks[rowIndex] ?? ''
      }
      return row
    })

    return { columns, dataSource, tableWidth: columns.length * COLUMN_WIDTH }
  }, [timepointTasks, extraTasks, timepointOrder, taskOrder, extraTaskOrder])

  if (columns.length === 0) return null

  return (
    <div className="mt-4">
      <Typography.Title level={3} className="mb-2 text-left">
        Sammanställning av insatser
      </Typography.Title>
      <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table
          tableLayout="fixed"
          size="small"
          bordered
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          style={{ width: tableWidth }}
        />
      </div>
    </div>
  )
}

export default TaskDistributionTable
